import React, { useState } from "react"
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js"
import { Box, Icons } from "@ridecell/web-components-material"
import { Ridecell } from "@ridecell/web-sdk"
import { useHistory } from "react-router-dom"
import { injectIntl } from "react-intl"

import LoadingButton from "../../../LoadingButton"
import PaymentMethod from "../AddedPaymentMethod"
import useGlobalContext from "../../../../utils/customHooks/useGlobalContext"
import messages from "../messages"

const SetupForm = ({
  handleNext,
  intl: { formatMessage },
  returnUrl,
  toggleBackdrop,
}) => {
  const {
    setError,
    state: { customer },
  } = useGlobalContext()
  const stripe = useStripe()
  const elements = useElements()
  const [step, setStep] = useState(0)
  const [addedCard, setAddedCard] = useState(null)
  const history = useHistory()

  const [isLoading, setIsLoading] = useState(false)
  let currentStep = history.location?.state?.currentStep
  let selectedProfileMode = history.location?.state?.selectedProfileMode
  let url =
    returnUrl ||
    `https://${window.location.host}/reserve/signup?q=confirm_payment`
  const query = new URLSearchParams(window.location.search)
  const clientSecret = query.get("setup_intent_client_secret")

  if (currentStep && !returnUrl) {
    url = `${url}&redirectedFrom=${currentStep}`
  }

  if (selectedProfileMode && !returnUrl) {
    url = `${url}&selectedProfileMode=${selectedProfileMode}`
  }

  React.useEffect(() => {
    ;(async () => {
      if (!stripe) return null
      // Retrieve the PaymentIntent and get the latest added card
      if (clientSecret) {
        try {
          toggleBackdrop(true)
          // It returns either error or setupIntent
          const response = await stripe.retrieveSetupIntent(clientSecret)
          if (response?.setupIntent) {
            if (response?.setupIntent?.status === "succeeded") {
              // All other possible statuses https://stripe.com/docs/api/errors#errors-setup_intent-status
              setStep(1)
              const cards = await Ridecell.getCards(customer?.id)
              const card = cards.find(
                (item) => item.card_id === response.setupIntent.payment_method
              )
              if (card) {
                setAddedCard(card)
              }
            }
          } else if (response.error) {
            setError({
              message:
                response.error?.message ||
                formatMessage(messages.somethingWentWrong),
            })
          }
        } finally {
          toggleBackdrop(false)
        }
      }
    })()
  }, [stripe])

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (clientSecret) {
      const queryParams = ""
      handleNext()
      history.replace({
        search: queryParams,
      })
      return
    }
    setIsLoading(true)

    if (!stripe || !elements) {
      return
    }

    const { error } = await stripe.confirmSetup({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: url,
      },
    })
    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setError({ message: error.message })
    } else {
      setStep(1)
    }
    setIsLoading(false)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box mb={"24px"}>
        {step === 0 ? (
          <PaymentElement />
        ) : (
          <PaymentMethod addedCard={addedCard} />
        )}
      </Box>
      <LoadingButton
        disabled={!stripe}
        fullWidth
        loading={isLoading}
        props={{
          fullWidth: true,
          endIcon: <Icons.ArrowForward />,
          size: "large",
        }}
        text={
          step ? formatMessage(messages.next) : formatMessage(messages.submit)
        }
        type="submit"
        variant="contained"
      />
    </form>
  )
}

export default injectIntl(SetupForm)
